import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { ArrowRight, Check, MapPin } from "@phosphor-icons/react";

/**
 * EspanolSEO.jsx — programmatic SPANISH landing pages for organic search.
 *
 * Two route families:
 *   /es/:slug                  → Spanish category landing (paired via hreflang
 *                                with its English /silent-auction-software twin)
 *   /es/ciudades/:citySlug     → Spanish city/geo landing
 *   /es                        → Spanish hub linking every ES page
 *
 * Copy is authored natively in Spanish (not machine-translated at runtime) and
 * rendered in the initial DOM so evergreen Googlebot indexes it on the first
 * render pass. SEOHead forces <html lang="es">, emits Spanish-paired hreflang,
 * canonical, and JSON-LD — guaranteeing clean crawlability without SSR.
 */

const ES_CATEGORIES = {
    "subastas-beneficas": {
        en: "/silent-auction-software",
        eyebrow: "Software de subastas silenciosas",
        title: "Software de subastas benéficas para organizaciones sin fines de lucro",
        h1: <>La plataforma todo en uno de <em className="text-accent">subastas silenciosas</em>.</>,
        lead: "Pujas desde el móvil, pujas automáticas, extensiones anti-sniping, marcadores en pantalla gigante y hojas de puja imprimibles cuando falla el Wi-Fi. Hecho para las subastas que de verdad financian tu misión.",
        hero: "https://images.unsplash.com/photo-1532954906987-87cb14d75e44?w=1600&h=900&fit=crop",
        hero_alt: "Artículos de subasta silenciosa sobre una mesa en una gala benéfica",
        keyword: "software de subastas benéficas",
        bullets: [
            "Pujas desde cualquier teléfono — sin descargar ninguna aplicación",
            "Puja automática (máxima) con avisos de superación por push y SMS",
            "Extensiones anti-sniping para que los últimos 30 segundos no decidan un lote de $10,000",
            "Hojas de puja imprimibles como respaldo si se cae el Wi-Fi",
            "Marcador en pantalla gigante con termómetro de recaudación animado",
            "Registro de auditoría sellado por voluntario en cada check-in y venta",
        ],
        body: [
            { h: "Para que las noches de evento dejen de dar miedo", p: "La mayoría de los problemas de una subasta silenciosa ocurren en los últimos 30 minutos: el Wi-Fi falla, se pierden las hojas de papel, los voluntarios no encuentran la hoja de cálculo. CharityFundraiser está construido para que nada de eso arruine tu noche. Las hojas de puja se imprimen al instante y el registro de auditoría te dice exactamente quién escaneó qué." },
            { h: "Listo en 4 minutos", p: "Sube los artículos por CSV (con plantilla incluida) o pégalos desde tu hoja de cálculo. La IA sugiere una puja inicial, un precio de reserva y un precio de compra inmediata para cada artículo. Personaliza tu tienda con tu logo y un color de acento. Listo." },
            { h: "Precios que no castigan tu gala", p: "Solo 2% sobre lo recaudado. Sin cuota de instalación. Sin suscripción mensual. Sin licencia por evento. Compáralo con los contratos anuales de $12K-$25K de otras plataformas y la cuenta sale a tu favor." },
        ],
        cta: "Realiza tu próxima subasta en CharityFundraiser",
    },
    "plataforma-galas-beneficas": {
        en: "/gala-fundraising-platform",
        eyebrow: "Plataforma para galas benéficas",
        title: "Plataforma para galas benéficas — boletos, subastas y paddle raise",
        h1: <>La plataforma construida alrededor de la <em className="text-accent">noche de gala</em>.</>,
        lead: "Boletos, patrocinios, subastas silenciosas y en vivo, paddle raise, liquidación posterior al evento y la única Guía de Coordinación con IA del mercado. Organiza una gala de $500K sin pegar con cinta cinco proveedores distintos.",
        hero: "https://images.unsplash.com/photo-1519671482749-fd09be7ccebf?w=1600&h=900&fit=crop",
        hero_alt: "Cena de gala de etiqueta con iluminación de escenario y una subasta en vivo",
        keyword: "plataforma para galas benéficas",
        bullets: [
            "Boletos por niveles (General · Patrón · VIP · Mesa de patrocinio) con códigos promocionales",
            "Paquetes de patrocinio con colocación de logo y matriz de beneficios",
            "Subasta silenciosa con pujas móviles + marcador",
            "Herramientas de subasta en vivo con niveles de paddle raise ($1000/500/250/100/50)",
            "Guía de Coordinación con IA: Run of Show automático, listas inteligentes y guiones para el subastador",
            "Check-in en la puerta con código QR y auditoría por voluntario",
        ],
        body: [
            { h: "Deja de dirigir tu gala desde una hoja de cálculo", p: "Una gala típica de $300K necesita una herramienta de subasta, una de boletos, una de correo y una hoja para reconciliar todo después. CharityFundraiser reúne las cinco en un solo panel con un único registro de donante por asistente. La Guía de Coordinación con IA incluso redacta tu Run of Show." },
            { h: "La recuperación posterior al evento que nunca tuviste", p: "Después de cada gala, CharityFundraiser genera automáticamente un informe de KPIs (recaudación, asistentes, donativo promedio, conversión). Tendencias Multi-Evento muestra los cambios año tras año de cada gala que hayas realizado." },
        ],
        cta: "Planifica tu próxima gala en CharityFundraiser",
    },
    "software-torneos-golf": {
        en: "/golf-tournament-fundraising-software",
        eyebrow: "Software para torneos de golf",
        title: "Software para torneos de golf benéficos — equipos, patrocinios, rifas",
        h1: <>La forma más rápida de <em className="text-accent">llenar tu torneo.</em></>,
        lead: "Gestión de cuartetos, patrocinios de hoyos, paquetes de mulligan, rifas, puntuación en tiempo real y subastas. Hecho específicamente para torneos de golf benéficos.",
        hero: "https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1600&h=900&fit=crop",
        hero_alt: "Campo de golf al amanecer con letreros de patrocinio en la calle",
        keyword: "software para torneos de golf benéficos",
        bullets: [
            "Armado de cuartetos — los capitanes invitan a sus propios jugadores",
            "Patrocinios por niveles: título, presentador, hoyo, carrito de bebidas",
            "Paquetes de mulligan, rifas y concursos (drive más largo, más cerca del hoyo)",
            "Subasta silenciosa de banderines de hoyo donados por los patrocinadores",
            "Check-in de salida tipo shotgun con código QR y auditoría por voluntario",
            "Marcador de puntuación en tiempo real proyectado en la pantalla del club",
        ],
        body: [
            { h: "Diseñado para quien lo ha organizado cinco años seguidos", p: "Los torneos benéficos son eventos recurrentes con patrocinadores y jugadores similares cada año. CharityFundraiser recuerda los cuartetos, patrocinios y artículos del año pasado para que este año el montaje tome minutos, no fines de semana." },
            { h: "Ventas de patrocinio que de verdad avanzan", p: "Paquetes de patrocinio con colocación de logo y matriz de beneficios. Envía un enlace de autoservicio a tus socios corporativos: eligen su nivel, suben su logo y pagan. Su logo se enruta a banderines, letreros, la página del evento y el folleto impreso." },
        ],
        cta: "Lanza tu torneo en CharityFundraiser",
    },
    "software-rifas-sin-fines-de-lucro": {
        en: "/raffle-software-for-nonprofits",
        eyebrow: "Software de rifas",
        title: "Software de rifas para organizaciones sin fines de lucro",
        h1: <>Vende más <em className="text-accent">boletos de rifa</em> y cumple la normativa.</>,
        lead: "Venta de boletos según licencia estatal, sorteos electrónicos, gestión de niveles de premios e informes de cumplimiento integrados. Organiza rifas 50/50, rifas de calendario y sorteos sin dolores de cabeza legales.",
        hero: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1600&h=900&fit=crop",
        hero_alt: "Boletos de rifa y mesa de exhibición de premios en un evento benéfico",
        keyword: "software de rifas para organizaciones sin fines de lucro",
        bullets: [
            "Venta de boletos configurable según las regulaciones de cada estado",
            "Sorteos electrónicos con registro de auditoría para cumplimiento",
            "Gestión de niveles de premios (1er lugar, 2do lugar, etc.)",
            "Rifas de calendario — sorteos diarios durante todo un mes",
            "Rifas 50/50 con termómetro de bote en tiempo real en la pantalla gigante",
            "Notificación automática al ganador por correo y SMS",
        ],
        body: [
            { h: "Venta de boletos consciente de la licencia", p: "Las leyes de rifas varían mucho por estado. CharityFundraiser admite reglas de compra por estado para que los voluntarios no vendan por error a un comprador de un estado sin licencia. Los registros de auditoría hacen que el informe anual de cumplimiento sea cosa de 5 minutos." },
            { h: "Integrado con el resto de tu evento", p: "La mayoría de las herramientas de rifa son independientes y reconcilias a mano. En CharityFundraiser las ventas de rifa sellan automáticamente el registro del comprador, así que la segmentación del próximo año ya sabe quién compró boletos este año." },
        ],
        cta: "Realiza tu rifa en CharityFundraiser",
    },
    "recaudacion-entre-pares": {
        en: "/peer-to-peer-fundraising-platform",
        eyebrow: "Recaudación entre pares (P2P)",
        title: "Plataforma de recaudación entre pares con tablas de líderes",
        h1: <>La plataforma <em className="text-accent">P2P</em> que tus recaudadores sí usarán.</>,
        lead: "Páginas personales con tu marca, tablas de líderes por equipo con termómetros en vivo, generación de recursos para redes sociales y campañas de donar-para-votar. La experiencia entre pares que tus seguidores querrán compartir.",
        hero: "https://images.unsplash.com/photo-1543351611-58f69d7c1781?w=1600&h=900&fit=crop",
        hero_alt: "Voluntarios con camisetas iguales en una caminata de recaudación entre pares",
        keyword: "plataforma de recaudación entre pares",
        bullets: [
            "Páginas personales por recaudador (foto, historia, meta)",
            "Tablas de líderes por equipo con termómetros que se actualizan en vivo",
            "Generación automática de tarjetas para redes (Open Graph + texto sugerido)",
            "Donaciones recurrentes ligadas a la campaña P2P",
            "Paneles Top-10 que tus recaudadores pueden capturar y compartir",
            "Campañas de donar-para-votar donde cada donativo suma votos",
        ],
        body: [
            { h: "Por qué fallan la mayoría de las herramientas P2P", p: "El P2P solo funciona cuando los recaudadores de verdad quieren compartir su página. La mayoría de las plataformas crean páginas feas que dan vergüenza publicar. Las páginas de CharityFundraiser coinciden con tu marca, generan hermosas tarjetas para redes y muestran estadísticas que los recaudadores quieren presumir." },
            { h: "Donar-para-votar y concursos temáticos", p: "¿Necesitas una campaña con concursantes (Perro Más Tierno, Generación '99 vs '00)? Donar-para-votar viene integrado. Cada dólar equivale a N votos, configurable, con conteo en vivo en la pantalla gigante." },
        ],
        cta: "Lanza tu campaña P2P",
    },
    "donar-por-mensaje-de-texto": {
        en: "/text-to-give-platform",
        eyebrow: "Donar por mensaje de texto",
        title: "Plataforma de donaciones por SMS — códigos cortos y bandeja compartida",
        h1: <>Donaciones por texto que de verdad <em className="text-accent">convierten.</em></>,
        lead: "Códigos cortos dedicados, enrutamiento por palabra clave, respuestas en una bandeja de entrada compartida y enlaces de donación de un toque. La experiencia de donar en 60 segundos que los donantes sí completan.",
        hero: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1600&h=900&fit=crop",
        hero_alt: "Pantalla de teléfono mostrando un mensaje para donar por SMS durante un evento benéfico",
        keyword: "donar por mensaje de texto",
        bullets: [
            "Códigos cortos dedicados con enrutamiento por palabra clave",
            "El donante envía monto + palabra clave → recibe al instante un enlace seguro",
            "Las respuestas se enrutan a una bandeja de entrada compartida del equipo",
            "Los donantes nuevos se inscriben automáticamente en el CRM con su atribución",
            "Pantalla gigante en el escenario: «Envía DONA al 53555 para dar»",
            "Mensaje de agradecimiento automático después del donativo",
        ],
        body: [
            { h: "Por qué importa donar por texto en el escenario", p: "Las peticiones en vivo tienen una ventana de 30 segundos en la que el donante decide dar. Donar por texto reduce esa ventana de «saca la tarjeta, busca el teléfono, busca la URL, escribe la dirección» a «envía dos palabras». Combinado con un marcador en pantalla convierte de 4 a 7 veces mejor que solo un código QR." },
            { h: "La bandeja compartida mantiene la conversación", p: "Cada respuesta a tu código corto llega a una bandeja de entrada compartida del equipo. Responde por SMS o correo desde una sola pantalla, con seguimiento de estado. Se acabó que «la línea de textos» sea el celular personal de alguien." },
        ],
        cta: "Agrega donaciones por texto a tu próximo evento",
    },
    "plataforma-de-donaciones-online": {
        en: "/online-donation-platform",
        eyebrow: "Plataforma de donaciones online",
        title: "Plataforma de donaciones online — recurrentes, tributo, donativo empresarial",
        h1: <>Páginas de donación que <em className="text-accent">completan el donativo.</em></>,
        lead: "Botón para cubrir la comisión, tributo y en memoria de, donaciones recurrentes, avisos de donativo empresarial y Apple Pay / Google Pay / PayPal junto al formulario de tarjeta. Hecho para el donante que ya escribió media docena de campos de dirección hoy.",
        hero: "https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=1600&h=900&fit=crop",
        hero_alt: "Donante entregando una tarjeta a un voluntario en un evento de recaudación",
        keyword: "plataforma de donaciones online",
        bullets: [
            "Montos sugeridos de un clic ($25 / $50 / $100 / $250 / Otro)",
            "Botón para cubrir la comisión (el donante absorbe Stripe + 2% y recibes el 100%)",
            "Botón de donación recurrente (mensual / trimestral / anual)",
            "Tributo / en honor de / en memoria de con correo de notificación a la familia",
            "Aviso de donativo empresarial igualado (compatible con Double the Donation)",
            "Apple Pay, Google Pay, Venmo y PayPal junto al formulario de tarjeta",
        ],
        body: [
            { h: "Cubrir la comisión, bien hecho", p: "Cada página de donación incluye un botón para cubrir la comisión, de modo que el donante absorbe el 2.9% + $0.30 del procesamiento y el 2% de la plataforma. Con el botón activado, tu organización recibe el 100% del donativo. Déjalo activado por defecto; quienes lo desactiven igual donan." },
            { h: "Recurrente, bien hecho", p: "La donación recurrente está junto al selector de monto, no a tres clics de distancia. Los donantes mensuales valen de 4 a 7 veces el valor anual de un donante único. Hazlo fácil y lo elegirán." },
        ],
        cta: "Lanza tu página de donación",
    },
};

const ES_CITIES = {
    "miami": { city: "Miami", state: "Florida" },
    "los-angeles": { city: "Los Ángeles", state: "California" },
    "houston": { city: "Houston", state: "Texas" },
    "san-antonio": { city: "San Antonio", state: "Texas" },
    "phoenix": { city: "Phoenix", state: "Arizona" },
    "nueva-york": { city: "Nueva York", state: "Nueva York" },
    "chicago": { city: "Chicago", state: "Illinois" },
    "dallas": { city: "Dallas", state: "Texas" },
    "el-paso": { city: "El Paso", state: "Texas" },
    "san-diego": { city: "San Diego", state: "California" },
};

export function getEsCategorySlugs() { return Object.keys(ES_CATEGORIES); }
export function getEsCitySlugs() { return Object.keys(ES_CITIES); }

const ORIGIN = () => (typeof window !== "undefined" ? window.location.origin : "");

function Shell({ testid, seo, eyebrow, h1, lead, hero, hero_alt, cta, bullets, body, crosslinks, crosstitle }) {
    return (
        <div className="bg-background min-h-screen" data-testid={testid}>
            <SEOHead {...seo} htmlLang="es" preLocalized includeSiteSchemas />
            <Navbar variant="marketing" />

            {/* HERO */}
            <section className="pt-32 pb-16 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground">
                <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6">
                        <p className="label-eyebrow text-accent">{eyebrow}</p>
                        <h1 className="font-heading text-4xl md:text-6xl mt-5 leading-[1.05]">{h1}</h1>
                        <p className="text-white/80 text-lg mt-6 max-w-xl leading-relaxed">{lead}</p>
                        <div className="flex flex-col sm:flex-row gap-3 mt-10">
                            <Link to="/signup-org" className="btn-accent" data-testid="es-cta-primary">{cta} <ArrowRight size={14} weight="bold" /></Link>
                            <Link to="/demo" className="btn-outline border-white/40 text-white hover:bg-white/10" data-testid="es-cta-demo">Prueba la demo</Link>
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <div className="aspect-[4/3] overflow-hidden border border-white/10">
                            <img src={hero} alt={hero_alt} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURES */}
            {bullets?.length ? (
                <section className="py-20 px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
                    <p className="label-eyebrow text-accent">Lo que incluye</p>
                    <h2 className="font-heading text-3xl md:text-5xl mt-4 max-w-3xl">Todo lo que tu equipo necesita, en un solo panel.</h2>
                    <ul className="grid md:grid-cols-2 gap-5 mt-12">
                        {bullets.map((b, i) => (
                            <li key={i} className="flex items-start gap-3 ed-card p-5" data-testid={`es-bullet-${i}`}>
                                <Check size={20} weight="bold" className="text-accent mt-0.5 shrink-0" />
                                <span className="text-base leading-relaxed">{b}</span>
                            </li>
                        ))}
                    </ul>
                </section>
            ) : null}

            {/* BODY */}
            {body?.length ? (
                <section className="py-20 px-6 md:px-12 lg:px-24 max-w-4xl mx-auto space-y-12">
                    {body.map((b, i) => (
                        <article key={i} className="border-l-4 border-accent pl-6" data-testid={`es-body-${i}`}>
                            <h3 className="font-heading text-2xl md:text-3xl text-primary">{b.h}</h3>
                            <p className="text-base text-foreground/75 mt-3 leading-relaxed">{b.p}</p>
                        </article>
                    ))}
                </section>
            ) : null}

            {/* CROSS-LINKS */}
            {crosslinks?.length ? (
                <section className="py-12 px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
                    <p className="label-eyebrow text-accent">{crosstitle || "También explora"}</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                        {crosslinks.map((l) => (
                            <Link key={l.to} to={l.to} className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-border hover:border-accent hover:text-accent transition-colors" data-testid={`es-xlink-${l.to}`}>
                                {l.label}
                            </Link>
                        ))}
                    </div>
                </section>
            ) : null}

            {/* CTA */}
            <section className="bg-primary text-primary-foreground py-24 px-6 text-center">
                <div className="max-w-2xl mx-auto">
                    <p className="label-eyebrow text-accent">Cuando estés listo</p>
                    <h2 className="font-heading text-4xl md:text-5xl mt-4">Activa tu plataforma en 4 minutos.</h2>
                    <p className="mt-6 text-white/75">Sin cuota de instalación ni mensualidad. Solo cobramos cuando tú recaudas.</p>
                    <Link to="/signup-org" className="btn-accent mt-10 inline-flex" data-testid="es-cta-bottom">{cta} <ArrowRight size={14} weight="bold" /></Link>
                </div>
            </section>
            <Footer />
        </div>
    );
}

export function EspanolCategoryPage() {
    const { slug } = useParams();
    const c = ES_CATEGORIES[slug];
    if (!c) return <Navigate to="/es" replace />;

    const base = ORIGIN();
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": c.title,
        "description": c.lead,
        "brand": { "@type": "Brand", "name": "CharityFundraiser" },
        "category": c.eyebrow,
        "offers": { "@type": "Offer", "priceCurrency": "USD", "price": "0", "description": "2% sobre lo recaudado. Sin instalación ni mensualidad." },
        "image": c.hero,
        "inLanguage": "es",
    };

    const crosslinks = Object.keys(ES_CATEGORIES).filter((s) => s !== slug).map((s) => ({
        to: `/es/${s}`, label: ES_CATEGORIES[s].eyebrow,
    }));

    return (
        <Shell
            testid={`es-category-${slug}`}
            seo={{
                title: `${c.title} | CharityFundraiser`,
                description: c.lead.slice(0, 160),
                keywords: [c.keyword, "recaudación de fondos sin fines de lucro", "software para organizaciones benéficas"],
                canonical: `/es/${slug}`,
                image: c.hero,
                jsonLd,
                alternates: [
                    ["es", base + `/es/${slug}`],
                    ["en", base + c.en],
                    ["x-default", base + c.en],
                ],
            }}
            eyebrow={c.eyebrow}
            h1={c.h1}
            lead={c.lead}
            hero={c.hero}
            hero_alt={c.hero_alt}
            cta={c.cta}
            bullets={c.bullets}
            body={c.body}
            crosslinks={crosslinks}
            crosstitle="También explora"
        />
    );
}

export function EspanolCityPage() {
    const { citySlug } = useParams();
    const c = ES_CITIES[citySlug];
    if (!c) return <Navigate to="/es" replace />;

    const base = ORIGIN();
    const title = `Software de recaudación de fondos en ${c.city}, ${c.state}`;
    const lead = `La plataforma todo en uno para organizaciones sin fines de lucro en ${c.city}: subastas silenciosas y en vivo, páginas de donación, boletos para eventos, recaudación entre pares y donaciones por texto — todo en español e inglés, con pagos directos a tu banco.`;
    const hero = "https://images.unsplash.com/photo-1531058020387-3be344556be6?w=1600&h=900&fit=crop";

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": title,
        "serviceType": "Plataforma de recaudación de fondos para organizaciones sin fines de lucro",
        "areaServed": { "@type": "City", "name": c.city },
        "provider": { "@type": "Organization", "name": "CharityFundraiser" },
        "description": lead,
        "inLanguage": "es",
    };

    const bullets = [
        `Pensado para las organizaciones benéficas y comunidades hispanas de ${c.city}`,
        "Páginas públicas bilingües (español e inglés) para llegar a más donantes",
        "Subastas silenciosas con pujas desde el móvil — sin descargar app",
        "Boletos y patrocinios para galas, torneos de golf y caminatas benéficas",
        "Donaciones por mensaje de texto y donaciones recurrentes",
        "Pagos directos a la cuenta de tu organización vía Stripe (sin intermediarios)",
    ];
    const body = [
        { h: `Recauda más en ${c.city} sin cinco proveedores distintos`, p: `Las organizaciones de ${c.city} suelen pagar por una herramienta de subastas, otra de boletos, otra de correo y una hoja de cálculo para cuadrar todo. CharityFundraiser reúne todo en un solo panel con un único registro de donante por persona, y tus páginas públicas se muestran automáticamente en español o inglés según el idioma del visitante.` },
        { h: "Sin cuota de instalación ni mensualidad", p: `Solo 2% sobre lo recaudado, más las comisiones estándar de procesamiento. Sin contratos anuales y sin licencias por evento. Activa tu plataforma para tu próximo evento en ${c.city} en solo unos minutos.` },
    ];
    const crosslinks = Object.keys(ES_CITIES).filter((s) => s !== citySlug).slice(0, 8).map((s) => ({
        to: `/es/ciudades/${s}`, label: ES_CITIES[s].city,
    }));

    return (
        <Shell
            testid={`es-city-${citySlug}`}
            seo={{
                title: `${title} | CharityFundraiser`,
                description: lead.slice(0, 160),
                keywords: [`recaudación de fondos ${c.city}`, `software sin fines de lucro ${c.city}`, "subastas benéficas", "donaciones online"],
                canonical: `/es/ciudades/${citySlug}`,
                image: hero,
                jsonLd,
                alternates: [
                    ["es", base + `/es/ciudades/${citySlug}`],
                    ["x-default", base + `/es/ciudades/${citySlug}`],
                ],
            }}
            eyebrow={`Recaudación de fondos · ${c.city}`}
            h1={<>Recaudación de fondos en <em className="text-accent">{c.city}</em>.</>}
            lead={lead}
            hero={hero}
            hero_alt={`Voluntarios de una organización sin fines de lucro en ${c.city}`}
            cta={`Empieza en ${c.city}`}
            bullets={bullets}
            body={body}
            crosslinks={crosslinks}
            crosstitle="Recaudación de fondos en otras ciudades"
        />
    );
}

export function EspanolHubPage() {
    const base = ORIGIN();
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "CharityFundraiser en español",
        "description": "Plataforma todo en uno de recaudación de fondos para organizaciones sin fines de lucro, en español e inglés.",
        "inLanguage": "es",
    };
    return (
        <div className="bg-background min-h-screen" data-testid="es-hub">
            <SEOHead
                title="CharityFundraiser en español — plataforma de recaudación de fondos sin fines de lucro"
                description="Subastas, donaciones, eventos, CRM y más para organizaciones sin fines de lucro — en español e inglés. Sin cuota de instalación ni mensualidad."
                keywords={["recaudación de fondos sin fines de lucro", "software de subastas benéficas", "plataforma de donaciones", "organizaciones benéficas"]}
                canonical="/es"
                jsonLd={jsonLd}
                htmlLang="es"
                preLocalized
                alternates={[["es", base + "/es"], ["en", base + "/"], ["x-default", base + "/"]]}
            />
            <Navbar variant="marketing" />
            <section className="pt-32 pb-12 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground">
                <div className="max-w-5xl mx-auto">
                    <p className="label-eyebrow text-accent">En español</p>
                    <h1 className="font-heading text-4xl md:text-6xl mt-5 leading-[1.05]">Recauda más para tu causa, <em className="text-accent">en español.</em></h1>
                    <p className="text-white/80 text-lg mt-6 max-w-2xl leading-relaxed">
                        La plataforma todo en uno para organizaciones sin fines de lucro: subastas silenciosas y en vivo,
                        páginas de donación, boletos para eventos, recaudación entre pares y donaciones por texto. Páginas
                        públicas bilingües y pagos directos a tu banco.
                    </p>
                    <Link to="/signup-org" className="btn-accent mt-10 inline-flex" data-testid="es-hub-cta">Empieza gratis <ArrowRight size={14} weight="bold" /></Link>
                </div>
            </section>

            <section className="py-16 px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
                <h2 className="font-heading text-3xl md:text-4xl text-primary">Soluciones</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
                    {Object.entries(ES_CATEGORIES).map(([s, c]) => (
                        <Link key={s} to={`/es/${s}`} className="ed-card p-6 hover:border-accent transition-colors block" data-testid={`es-hub-cat-${s}`}>
                            <p className="label-eyebrow text-accent">{c.eyebrow}</p>
                            <p className="text-base text-foreground/75 mt-3 leading-relaxed">{c.lead.slice(0, 120)}…</p>
                            <span className="text-accent text-sm font-bold mt-4 inline-flex items-center gap-1">Ver más <ArrowRight size={12} weight="bold" /></span>
                        </Link>
                    ))}
                </div>
            </section>

            <section className="py-16 px-6 md:px-12 lg:px-24 max-w-6xl mx-auto">
                <h2 className="font-heading text-3xl md:text-4xl text-primary">Por ciudad</h2>
                <div className="flex flex-wrap gap-2 mt-8">
                    {Object.entries(ES_CITIES).map(([s, c]) => (
                        <Link key={s} to={`/es/ciudades/${s}`} className="text-sm font-bold px-4 py-2.5 border border-border hover:border-accent hover:text-accent transition-colors inline-flex items-center gap-2" data-testid={`es-hub-city-${s}`}>
                            <MapPin size={14} weight="bold" /> {c.city}
                        </Link>
                    ))}
                </div>
            </section>
            <Footer />
        </div>
    );
}
